import React, { useMemo } from 'react';
import { DateInput } from '@src/components/ui/react-hook-form/date-input/date-input';
import { TimeInput } from '@src/components/ui/react-hook-form/time-input/time-input';
import PropTypes from 'prop-types';
import moment from 'moment';
import { getRestDayEvents } from '@src/utils/date/getRestDayEvents';
import { getBlockedDayEvents } from '@src/utils/date/getBlockedDayEvents';
import { useFormContext, useWatch } from 'react-hook-form';

const normalizeUnavailables = blockedDates =>
    blockedDates?.map(block => ({
        ...block,
        StartDateTime: moment.unix(block.StartDateTime),
        EndDateTime: moment.unix(block.EndDateTime)
    })) ?? [];

const processFullDayEvent = (event, fullDayRules) => {
    if (event.daysOfWeek?.length) {
        event.daysOfWeek.forEach(day => fullDayRules.blockedDaysOfWeek.add(day));
    } else if (event.start && event.end) {
        const start = moment(event.start).startOf('day');
        const end = moment(event.end).startOf('day');
        const current = start.clone();
        while (current.isSameOrBefore(end)) {
            fullDayRules.blockedDates.add(current.format('YYYY-MM-DD'));
            current.add(1, 'day');
        }
    }
};

const processTimeBasedEvent = (event, timeRules) => {
    if (event.daysOfWeek?.length) {
        event.daysOfWeek.forEach(day => {
            if (!timeRules.recurring[day]) timeRules.recurring[day] = [];
            timeRules.recurring[day].push({
                startTime: event.startTime,
                endTime: event.endTime
            });
        });
    } else if (event.start && event.end) {
        const dayKey = moment(event.start).format('YYYY-MM-DD');
        if (!timeRules.absolute[dayKey]) timeRules.absolute[dayKey] = [];
        timeRules.absolute[dayKey].push({
            startTime: moment(event.start).format('HH:mm:ss'),
            endTime: moment(event.end).format('HH:mm:ss')
        });
    }
};

export const DateTimeField = ({ openingHours, blockedDates, allDay }) => {
    const { setValue, clearErrors } = useFormContext();
    const selectedDate = useWatch({ name: 'date' });

    const disabledDates = useMemo(() => {
        const availables = allDay ? [] : openingHours;
        const unavailables = normalizeUnavailables(blockedDates);

        const fullDayRules = {
            blockedDaysOfWeek: new Set(),
            blockedDates: new Set()
        };

        const timeRules = {
            recurring: {},
            absolute: {}
        };

        const allEvents = [...getRestDayEvents(availables), ...getBlockedDayEvents(unavailables)];

        allEvents.forEach(event => {
            if (event.allDay) {
                processFullDayEvent(event, fullDayRules);
            } else {
                processTimeBasedEvent(event, timeRules);
            }
        });

        return {
            blockedDaysOfWeek: [...fullDayRules.blockedDaysOfWeek],
            blockedDates: fullDayRules.blockedDates,
            disabledTime: timeRules
        };
    }, [allDay, openingHours, blockedDates]);

    const minTime = useMemo(() => {
        if (!selectedDate) return null;
        const today = moment().startOf('day');
        const selected = moment(selectedDate).startOf('day');
        return today.isSame(selected)
            ? moment()
                  .seconds(0)
                  .milliseconds(0) // Now, rounded to minute
            : null;
    }, [selectedDate]);

    const handleDateChange = () => {
        const timer = setTimeout(() => {
            setValue('time', null);
            clearErrors('time');
            clearTimeout(timer);
        }, 0);
    };

    return (
        <>
            <div>
                <span className='tw-font-medium'>Select Date</span>
                <DateInput
                    name='date'
                    className='tw-w-full'
                    disablePast
                    minDate={moment().add(1, 'day')}
                    disabledDates={disabledDates}
                    onChange={handleDateChange}
                    InputProps={{
                        className: 'tw-bg-white'
                    }}
                />
            </div>
            <div>
                <span className='tw-font-medium'>Select Time</span>
                <TimeInput
                    name='time'
                    className='tw-w-full'
                    selectedDate={selectedDate}
                    disabledTime={disabledDates.disabledTime}
                    minTime={minTime}
                    InputProps={{
                        className: 'tw-bg-white',
                        placeholder: '12:00 AM'
                    }}
                />
            </div>
        </>
    );
};

DateTimeField.propTypes = {
    openingHours: PropTypes.arrayOf(
        PropTypes.shape({
            Day: PropTypes.number,
            IsRestDay: PropTypes.bool,
            StartTime: PropTypes.string,
            EndTime: PropTypes.string
        })
    ),
    blockedDates: PropTypes.arrayOf(
        PropTypes.shape({
            StartDateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            EndDateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
        })
    ),
    allDay: PropTypes.bool
};
